// @flow
import {
  Button,
  Cascader,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useMemo } from 'react';

import {
  CAMPAIGN_ES_FIELDS,
  CAMPAIGN_MORE_FILTERS_KEYS,
  CHANNEL_DISPLAY_NAMES,
  CHANNEL_GROUP_CHANNELS,
  CHANNEL_GROUP_DISPLAY_NAMES,
  CHANNEL_GROUPS,
  HUMAN_READ_DATE_FORMAT,
  TRIDENT_CAMPAIGN_TEMPLATE,
  TRIDENT_CAMPAIGN_TEMPLATE_DISPLAY_NAMES,
} from 'omega-rtc/constants/ucd';
import { getFilterValue } from 'omega-rtc/utils/ucd-utils';
import AudienceTypeSelector from 'omega-rtc/components/custom/entities/AudienceTypeSelector';

type Props = {
  open: boolean,
  moreFiltersInitialValue: Array,
  updateFilterCount: Function,
  closeFilter: Function,
  onAction: Function,
};

const channelOptions = Object.keys(CHANNEL_GROUPS).map((channelGroupKey) => {
  return {
    label: CHANNEL_GROUP_DISPLAY_NAMES[channelGroupKey],
    value: channelGroupKey,
    children: CHANNEL_GROUP_CHANNELS[channelGroupKey].map((channelKey) => ({
      label: CHANNEL_DISPLAY_NAMES[channelKey],
      value: channelKey,
    })),
  };
});

const templateTypeOptions = Object.keys(TRIDENT_CAMPAIGN_TEMPLATE)
  .filter((key) => key !== 'fromBlank')
  .map((key) => ({
    label: TRIDENT_CAMPAIGN_TEMPLATE_DISPLAY_NAMES[key],
    value: TRIDENT_CAMPAIGN_TEMPLATE[key],
  }));

const CampaignMoreFilters = (props: Props) => {
  const [form] = Form.useForm();

  const setDefaultValues = () => {
    form.setFieldsValue({
      [CAMPAIGN_MORE_FILTERS_KEYS.AUDIENCE]: [],
      [CAMPAIGN_MORE_FILTERS_KEYS.START]: '',
      [CAMPAIGN_MORE_FILTERS_KEYS.END]: '',
      [CAMPAIGN_MORE_FILTERS_KEYS.TEMPLATE_TYPE]: [],
      [CAMPAIGN_MORE_FILTERS_KEYS.CREATOR]: '',
      [CAMPAIGN_MORE_FILTERS_KEYS.CHANNEL]: '',
    });
  };

  const onReset = () => {
    props.onAction([], 'reset');
    setDefaultValues();
  };

  const onApply = () => {
    const response = [];

    const audienceTypes = form.getFieldValue(
      CAMPAIGN_MORE_FILTERS_KEYS.AUDIENCE,
    );
    const startDate = form.getFieldValue(CAMPAIGN_MORE_FILTERS_KEYS.START);
    const endDate = form.getFieldValue(CAMPAIGN_MORE_FILTERS_KEYS.END);
    const templateType = form.getFieldValue(
      CAMPAIGN_MORE_FILTERS_KEYS.TEMPLATE_TYPE,
    );
    const createdBy = form.getFieldValue(CAMPAIGN_MORE_FILTERS_KEYS.CREATOR);
    const chosenChannel = form.getFieldValue(
      CAMPAIGN_MORE_FILTERS_KEYS.CHANNEL,
    );

    const channel = chosenChannel ? chosenChannel[1] : '';
    const channelGroup = chosenChannel ? chosenChannel[0] : '';

    if (!_.isEmpty(audienceTypes)) {
      response.push({
        lhs: CAMPAIGN_ES_FIELDS.audience,
        operator: 'in',
        rhs: audienceTypes,
      });
    }

    if (!_.isEmpty(channelGroup)) {
      response.push({
        lhs: CAMPAIGN_ES_FIELDS.channelGroup,
        operator: 'eq',
        rhs: channelGroup,
      });
    }
    if (!_.isEmpty(channel)) {
      response.push({
        lhs: CAMPAIGN_ES_FIELDS.channels,
        operator: 'eq',
        rhs: channel,
      });
    }
    if (!_.isEmpty(createdBy)) {
      response.push({
        lhs: CAMPAIGN_ES_FIELDS.creator,
        operator: 'eq',
        rhs: createdBy,
      });
    }
    if (!_.isEmpty(startDate)) {
      response.push({
        lhs: CAMPAIGN_ES_FIELDS.start,
        operator: 'eq',
        rhs: startDate,
      });
    }
    if (!_.isEmpty(endDate)) {
      response.push({
        lhs: CAMPAIGN_ES_FIELDS.end,
        operator: 'eq',
        rhs: endDate,
      });
    }
    if (!_.isEmpty(templateType)) {
      response.push({
        lhs: CAMPAIGN_ES_FIELDS.templateType,
        operator: 'eq',
        rhs: templateType,
      });
    }
    props.onAction(response, 'apply');
  };

  const initialStartDate = useMemo(
    () =>
      getFilterValue(
        props.moreFiltersInitialValue,
        CAMPAIGN_ES_FIELDS.start,
        '',
      ),
    [props.moreFiltersInitialValue],
  );

  const initialEndDate = useMemo(
    () =>
      getFilterValue(props.moreFiltersInitialValue, CAMPAIGN_ES_FIELDS.end, ''),
    [props.moreFiltersInitialValue],
  );

  const initialValues = {
    audienceTypes: getFilterValue(
      props.moreFiltersInitialValue,
      CAMPAIGN_ES_FIELDS.audience,
      [],
    ),
    channel: getFilterValue(
      props.moreFiltersInitialValue,
      CAMPAIGN_ES_FIELDS.channels,
      '',
    ),
    channelGroup: getFilterValue(
      props.moreFiltersInitialValue,
      CAMPAIGN_ES_FIELDS.channelGroup,
      '',
    ),
    createdBy: getFilterValue(
      props.moreFiltersInitialValue,
      CAMPAIGN_ES_FIELDS.creator,
      '',
    ),
    startDate: initialStartDate ? dayjs(initialStartDate) : '',
    endDate: initialEndDate ? dayjs(initialEndDate) : '',
    templateType: getFilterValue(
      props.moreFiltersInitialValue,
      CAMPAIGN_ES_FIELDS.templateType,
    ),
  };

  return (
    <Modal
      size={'mini'}
      open={props.open}
      centered
      width={720}
      title="More filters"
      onCancel={props.closeFilter}
      footer={[
        <Button
          onClick={onReset}
          type="text"
          color="primary"
          style={{ fontWeight: '600' }}
        >
          Reset
        </Button>,
        <Button
          onClick={onApply}
          type="primary"
          style={{ fontWeight: '600', marginLeft: '10px' }}
        >
          Apply
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[24, 20]}>
          <Col span={12}>
            <Form.Item
              name={CAMPAIGN_MORE_FILTERS_KEYS.AUDIENCE}
              label="Audience Type"
              initialValue={initialValues.audienceTypes}
            >
              <AudienceTypeSelector multiple />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Channel Type"
              name={CAMPAIGN_MORE_FILTERS_KEYS.CHANNEL}
              initialValue={initialValues.channel}
            >
              <Cascader
                options={channelOptions}
                dropdownMenuColumnStyle={{ height: '20%' }}
                placeholder="Select Channel Type"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={CAMPAIGN_MORE_FILTERS_KEYS.START}
              label="Start Date"
              initialValue={initialValues.startDate}
            >
              <DatePicker
                style={{
                  width: '100%',
                }}
                format={HUMAN_READ_DATE_FORMAT}
                disabledDate={(current) => {
                  const endDate = form.getFieldValue(
                    CAMPAIGN_MORE_FILTERS_KEYS.END,
                  );
                  return (
                    endDate &&
                    current.startOf('day') > dayjs(endDate).startOf('day')
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={CAMPAIGN_MORE_FILTERS_KEYS.END}
              label="End Date"
              initialValue={initialValues.endDate}
            >
              <DatePicker
                style={{
                  width: '100%',
                  borderRadius: '14px',
                }}
                disabledDate={(current) => {
                  const startDate = form.getFieldValue(
                    CAMPAIGN_MORE_FILTERS_KEYS.START,
                  );
                  return (
                    startDate &&
                    current.startOf('day') < dayjs(startDate).startOf('day')
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={CAMPAIGN_MORE_FILTERS_KEYS.CREATOR}
              label="Created by"
              initialValue={initialValues.createdBy}
            >
              <Input
                style={{
                  width: '100%',
                  borderRadius: '14px',
                }}
                placeholder={'Enter an email address'}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={CAMPAIGN_MORE_FILTERS_KEYS.TEMPLATE_TYPE}
              label="Template Type"
              initialValue={initialValues.templateType}
            >
              <Select
                placeholder="Template Type"
                options={templateTypeOptions}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CampaignMoreFilters;
